import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type {
  ControllerPlacementCandidateTable,
  OpeningControllerPlacementCandidate,
} from "building-types/electrical/candidate";
import { CandidateType, ControllerPlacementPass } from "building-types/electrical/candidateTypesEnum";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import type { RoomIdToRoomSetIndexLookup } from "doc/rooms/roomSetTypes";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { makeDoorOperationInfo } from "../../doors/doorOperationInfo";
import { isGarageDoorOperationType } from "../../doors/doorUtils";
import { WallTraverseSystem } from "../../walls/wallTraversal/wallTraverseSystem";
import { TraverseSide } from "../../walls/wallTraversal/wallTraverseTypes";
import { addCandidateToTable } from "./addCandidateToTable";

export enum InsertionWindingDirection {
  FORWARD = "FORWARD",
  BACKWARD = "BACKWARD",
  CENTER = "CENTER",
}

interface OpeningCandidateNewOpts {
  readonly levelId: string;
  readonly roomId: string;
  readonly sourceId: string;
  readonly toRoomId: string | undefined;
  readonly insertionPass: ControllerPlacementPass;
  readonly insertionDirection: InsertionWindingDirection;
  readonly x: number;
  readonly y: number;
  readonly nx: number;
  readonly ny: number;
}

export function openingCandidateNew({
  levelId,
  roomId,
  sourceId,
  toRoomId,
  insertionPass,
  insertionDirection,
  x,
  y,
  nx,
  ny,
}: OpeningCandidateNewOpts): OpeningControllerPlacementCandidate {
  return {
    id: uid(),
    type: CandidateType.OPENING,
    levelId,
    roomId,
    sourceId,
    toRoomId,
    position: [x, y],
    insertionPass,
    insertionDirection,
    normal: [nx, ny],
    tags: {},
  };
}

/**
 * Create controller candidates by starting a doors and traversing the walls in both directions.
 */
interface CandidatesOpeningNewParams {
  doors: Table<GeneratedDoorPlacement>;
  planarGraphs: Table<PolygonPlanarGraph>;
  wallSystems: Table<WallSystem>;
  wallMassSystems: Table<WallMassSystem>;
  profileSource: ActiveProfileSource;
  doorSettings: DoorSettings;
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup;
  candidateTable: ControllerPlacementCandidateTable;
}

export function candidatesOpeningNew({
  doors,
  planarGraphs,
  wallSystems,
  wallMassSystems,
  profileSource,
  doorSettings,
  roomIdToRoomSetIndexLookup,
  candidateTable,
}: CandidatesOpeningNewParams) {
  const doorList = filterDoors(doors, wallSystems);

  if (doorList.length === 0) {
    return;
  }

  const wallTraverseSystem = new WallTraverseSystem(
    planarGraphs,
    wallSystems,
    wallMassSystems,
    doorSettings,
    profileSource,
  );

  for (const door of doorList) {
    const doorInfo = makeDoorOperationInfo(door);

    const {
      frontLeftIsLatchSide,
      frontRightIsLatchSide,
      frontLeftSwing,
      frontRightSwing,
      backLeftSwing,
      backRightSwing,
    } = doorInfo
      ? doorInfo
      : {
          frontLeftIsLatchSide: false,
          frontRightIsLatchSide: false,
          frontLeftSwing: undefined,
          frontRightSwing: undefined,
          backLeftSwing: undefined,
          backRightSwing: undefined,
        };

    const { leftResult: frontLeftResult, rightResult: frontRightResult } =
      wallTraverseSystem.traverseBothDirectionsFromEntity(door, TraverseSide.FRONT);

    // result can be null if no suitable position was found during the traverse
    if (frontLeftResult) {
      const { originX: x, originY: y, dirX: nx, dirY: ny } = frontLeftResult;
      const isLatchSide = frontLeftIsLatchSide;
      const isSwingSide = !!frontLeftSwing || !!backRightSwing;
      const controllerPlacementPass = getControllerPlacementPassType(isSwingSide, isLatchSide);

      const roomSetIndex = door.frontRoom ? roomIdToRoomSetIndexLookup[door.frontRoom.id] : undefined;
      if (door.frontRoom && roomSetIndex !== undefined) {
        addCandidateToTable(
          openingCandidateNew({
            levelId: door.frontRoom.level?.id,
            roomId: door.frontRoom.id,
            sourceId: door.id,
            toRoomId: door.backRoom?.id,
            insertionPass: controllerPlacementPass,
            insertionDirection: InsertionWindingDirection.FORWARD,
            x,
            y,
            nx,
            ny,
          }),
          candidateTable,
          roomSetIndex,
        );
      }
    }

    // result can be null if no suitable position was found during the traverse
    if (frontRightResult) {
      const { originX: x, originY: y, dirX: nx, dirY: ny } = frontRightResult;
      const isLatchSide = frontRightIsLatchSide;
      const isSwingSide = !!frontRightSwing || !!backLeftSwing;
      const controllerPlacementPass = getControllerPlacementPassType(isSwingSide, isLatchSide);

      const roomSetIndex = door.frontRoom ? roomIdToRoomSetIndexLookup[door.frontRoom.id] : undefined;
      if (roomSetIndex !== undefined && door.frontRoom) {
        addCandidateToTable(
          openingCandidateNew({
            levelId: door.frontRoom.level?.id,
            roomId: door.frontRoom.id,
            sourceId: door.id,
            toRoomId: door.backRoom?.id,
            insertionPass: controllerPlacementPass,
            insertionDirection: InsertionWindingDirection.BACKWARD,
            x,
            y,
            nx,
            ny,
          }),
          candidateTable,
          roomSetIndex,
        );
      }
    }

    const { leftResult: backLeftResult, rightResult: backRightResult } =
      wallTraverseSystem.traverseBothDirectionsFromEntity(door, TraverseSide.BACK);

    // result can be null if no suitable position was found during the traverse
    if (backLeftResult) {
      const { originX: x, originY: y, dirX: nx, dirY: ny } = backLeftResult;
      const isLatchSide = frontRightIsLatchSide;
      const isSwingSide = !!backLeftSwing || !!frontRightSwing;
      const controllerPlacementPass = getControllerPlacementPassType(isSwingSide, isLatchSide);

      const roomSetIndex = door.backRoom ? roomIdToRoomSetIndexLookup[door.backRoom?.id] : undefined;
      if (roomSetIndex !== undefined) {
        addCandidateToTable(
          openingCandidateNew({
            levelId: door.backRoom?.level?.id,
            roomId: door.backRoom?.id,
            sourceId: door.id,
            toRoomId: door.frontRoom?.id,
            insertionPass: controllerPlacementPass,
            insertionDirection: InsertionWindingDirection.FORWARD,
            x,
            y,
            nx,
            ny,
          }),
          candidateTable,
          roomSetIndex,
        );
      }
    }

    // result can be null if no suitable position was found during the traverse
    if (backRightResult) {
      const { originX: x, originY: y, dirX: nx, dirY: ny } = backRightResult;
      const isLatchSide = frontLeftIsLatchSide;
      const isSwingSide = !!backRightSwing || !!frontLeftSwing;
      const controllerPlacementPass = getControllerPlacementPassType(isSwingSide, isLatchSide);

      const roomSetIndex = door.backRoom ? roomIdToRoomSetIndexLookup[door.backRoom?.id] : undefined;
      if (roomSetIndex !== undefined) {
        addCandidateToTable(
          openingCandidateNew({
            levelId: door.backRoom?.level?.id,
            roomId: door.backRoom?.id,
            sourceId: door.id,
            toRoomId: door.frontRoom?.id,
            insertionPass: controllerPlacementPass,
            insertionDirection: InsertionWindingDirection.BACKWARD,
            x,
            y,
            nx,
            ny,
          }),
          candidateTable,
          roomSetIndex,
        );
      }
    }
  }
}

function getControllerPlacementPassType(isSwingSide: boolean, isLatchSide: boolean) {
  if (isSwingSide) {
    return ControllerPlacementPass.BY_DOOR_BEHIND_SWING;
  }
  if (isLatchSide) {
    return ControllerPlacementPass.BY_DOOR_LATCH_SIDE;
  }
  return ControllerPlacementPass.BY_DOOR;
}

// Filter out doors that are not suitable for controller placement
function filterDoors(doorTable: Table<GeneratedDoorPlacement>, wallSystems: Table<WallSystem>) {
  const doors: GeneratedDoorPlacement[] = [];
  for (const doorId in doorTable) {
    const door = doorTable[doorId];

    if (!door?.opening?.planExtents) {
      continue;
    }

    if (isGarageDoorOperationType(door)) {
      continue;
    }

    // Currently, only doors with rooms on both sides are considered
    const room = door.frontRoom;
    if (!room?.level) {
      continue;
    }
    const backRoom = door.backRoom;
    if (!backRoom) {
      continue;
    }

    const wallSystem = wallSystems[room.level.id];

    // OPEN-FULL VOID WALLS have recently been considered doors and added to the doors table, they need to be filtered out
    if (wallSystem.walls[wallSystem.halfEdgeToWall[door.halfEdgeIndex]].wallType === WallType.VOID) {
      continue;
    }

    doors.push(door);
  }
  return doors;
}
